import React, { useState, useEffect, useCallback } from 'react';
import CommonSelect from '../../Common/CommonSelect';
import InlineFormRow from './InlineFormRow';
import ZohoAccountMappingEditModal from './ZohoAccountMappingEditModal';
import zohoService from '../../../services/zohoService';
import { toast } from 'react-toastify';
import {
  displayLabel,
  formatMappedAccountLabel,
  sortByXeroCode,
} from './zohoMappingUtils';
import {
  AutoMappedFooterStrip,
  MappedValuePill,
  splitMappingFields,
  UniformRefBadge,
} from './ZohoMappingUi';

const COLUMN_SPLIT_AT = 7;
const COLUMN_GAP_PX = 14;

const StatusDot = ({ mapped }) => (
  <span
    style={{
      display: 'inline-block',
      width: 6,
      height: 6,
      borderRadius: '50%',
      background: mapped ? 'var(--bs-primary)' : 'var(--bs-secondary-bg)',
      flexShrink: 0,
    }}
    aria-hidden
  />
);

const ZohoMappingsCard = ({
  isConnected,
  zohoTenant,
  setZohoTenant,
  accountMappings,
  setAccountMappings,
  mappingDefinitions,
  onRefresh,
}) => {
  const [organizations, setOrganizations] = useState([]);
  const [cacheStatus, setCacheStatus] = useState(null);
  const [isFetchingOrg, setIsFetchingOrg] = useState(false);
  const [isLoadingCacheStatus, setIsLoadingCacheStatus] = useState(false);
  const [savingField, setSavingField] = useState(null);
  const [showAccountEditModal, setShowAccountEditModal] = useState(false);

  const enabled = isConnected && zohoTenant?.id;

  const loadCacheStatus = useCallback(async () => {
    if (!enabled) {
      setCacheStatus(null);
      return;
    }

    try {
      setIsLoadingCacheStatus(true);
      const status = await zohoService.getReferenceCacheStatus();
      setCacheStatus(status);
    } catch {
      setCacheStatus(null);
    } finally {
      setIsLoadingCacheStatus(false);
    }
  }, [enabled]);

  const allAccountFields = mappingDefinitions?.sections
    ? mappingDefinitions.sections.flatMap((s) => s.fields)
    : [];

  useEffect(() => {
    if (isConnected) {
      const fetchOrganizations = async () => {
        try {
          setIsFetchingOrg(true);
          const response = await zohoService.getOrganizations();
          const orgs = Array.isArray(response?.data) ? response.data : [];
          setOrganizations(orgs);
        } catch (error) {
          setOrganizations([]);
          toast.error(error.message || 'Error fetching Zoho organizations', {
            position: 'top-right',
            autoClose: 3000,
          });
        } finally {
          setIsFetchingOrg(false);
        }
      };
      fetchOrganizations();
    } else {
      setOrganizations([]);
    }
  }, [isConnected]);

  useEffect(() => {
    loadCacheStatus();
  }, [loadCacheStatus]);

  const handleRefreshOrganizations = async () => {
    try {
      setIsFetchingOrg(true);
      const status = await zohoService.refreshReferenceCache();
      setCacheStatus(status);
      const response = await zohoService.getOrganizations();
      const orgs = Array.isArray(response?.data) ? response.data : [];
      setOrganizations(orgs);
      toast.success('Organizations and reference data refreshed', {
        position: 'top-right',
        autoClose: 2000,
      });
    } catch (error) {
      toast.error(error.message || 'Error refreshing organizations', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsFetchingOrg(false);
    }
  };

  const handleOrganizationChange = async (value) => {
    const previousValue = zohoTenant;

    if (value?.id === previousValue?.id) {
      return;
    }

    setZohoTenant(value);
    setSavingField('zoho_tenant');

    try {
      await zohoService.savePartialSettings({ zoho_tenant: value });
      toast.success('Organization saved', { position: 'top-right', autoClose: 2000 });
      setCacheStatus(null);
      await loadCacheStatus();
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      setZohoTenant(previousValue);
      toast.error(error.message || 'Failed to save organization', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setSavingField(null);
    }
  };

  const handleAccountMappingsSaved = async (response) => {
    const updated = {};
    allAccountFields.forEach(({ key }) => {
      if (Object.prototype.hasOwnProperty.call(response, key)) {
        updated[key] = response[key];
      }
    });

    if (Object.keys(updated).length > 0) {
      setAccountMappings((prev) => ({ ...prev, ...updated }));
    }

    if (onRefresh) {
      await onRefresh();
    }
  };

  const isSubmitting = savingField !== null;
  const sortedAccountFields = sortByXeroCode(allAccountFields);
  const [leftFields, rightFields] = splitMappingFields(sortedAccountFields, COLUMN_SPLIT_AT);

  const renderMappingRow = (field, index) => {
    const mapped = Boolean(accountMappings[field.key]?.id);
    const mappedLabel = formatMappedAccountLabel(
      accountMappings[field.key],
      field.xero_code
    );
    const label = displayLabel(field.label);

    return (
      <div
        key={field.key}
        className="d-flex align-items-center gap-2"
        style={{
          padding: '6px 8px',
          borderRadius: 4,
          background: index % 2 === 0 ? 'var(--bs-tertiary-bg)' : 'transparent',
        }}
      >
        <StatusDot mapped={mapped} />
        {field.xero_code ? <UniformRefBadge code={field.xero_code} /> : <span style={{ width: 52, flexShrink: 0 }} />}
        <span
          className="small fw-medium flex-grow-1"
          style={{ minWidth: 0, lineHeight: 1.35 }}
          title={label}
        >
          {label}
        </span>
        <div style={{ flexShrink: 0, maxWidth: '42%', minWidth: 0 }}>
          <MappedValuePill mapped={mapped} label={mappedLabel} />
        </div>
      </div>
    );
  };

  const renderMappingColumn = (fields) => (
    <div>{fields.map((field, index) => renderMappingRow(field, index))}</div>
  );

  const cacheCounts = cacheStatus?.counts ?? {};
  const hasCache = Boolean(cacheStatus?.refreshed_at);
  const cacheMatchesOrg = !cacheStatus?.organization_id
    || String(cacheStatus.organization_id) === String(zohoTenant?.id ?? '');

  return (
    <div className={`card mb-3 ${!enabled ? 'opacity-75' : ''}`}>
      <div className="card-body py-2 px-3">
        <h5 className="card-title mb-3">Mapping</h5>

        {!enabled ? (
          <p className="text-muted small mb-3">
            Connect your Zoho account and select an organization to configure mappings.
          </p>
        ) : (
          <>
            <div className="row g-2 mb-4">
              <div className="col-md-6">
                <InlineFormRow label="Organization" labelWidth={130}>
                  <CommonSelect
                    value={zohoTenant}
                    onChange={handleOrganizationChange}
                    options={organizations}
                    isSubmitting={isFetchingOrg || savingField === 'zoho_tenant'}
                    errors={{}}
                    placeholder="Select organization…"
                    actionButtons={[
                      {
                        icon: 'fa fa-refresh',
                        label: 'Refresh cache',
                        title: 'Refresh organizations, accounts, taxes, tags, and currencies from Zoho',
                        className: 'btn-primary btn-sm',
                        onClick: handleRefreshOrganizations,
                      },
                    ]}
                    className=""
                  />
                </InlineFormRow>
              </div>
              <div className="col-12">
                <div
                  className="border rounded px-3 py-2 small"
                  style={{ background: 'var(--bs-tertiary-bg)' }}
                >
                  <div className="fw-medium text-body mb-1">Reference data cache</div>
                  <p className="text-muted mb-2">
                    Caches organizations, chart of accounts, taxes, reporting tags, currencies, and
                    organization metadata from Zoho Books to reduce API calls during sync and mapping.
                  </p>
                  {isLoadingCacheStatus && !cacheStatus ? (
                    <span className="text-muted">
                      <span className="spinner-border spinner-border-sm me-1" />
                      Loading cache status…
                    </span>
                  ) : (
                    <>
                      <div className="d-flex flex-wrap gap-3 mb-1">
                        <span>
                          <strong>Last refreshed:</strong>{' '}
                          {hasCache ? cacheStatus.refreshed_at : 'Never'}
                        </span>
                        {hasCache && !cacheMatchesOrg && (
                          <span className="text-warning">
                            Cache is for a different organization — refresh to update.
                          </span>
                        )}
                      </div>
                      <div className="d-flex flex-wrap gap-3 text-muted">
                        <span><strong>Organizations:</strong> {cacheCounts.organizations ?? 0}</span>
                        <span><strong>Accounts:</strong> {cacheCounts.accounts ?? 0}</span>
                        <span><strong>Taxes:</strong> {cacheCounts.taxes ?? 0}</span>
                        <span><strong>Tags:</strong> {cacheCounts.tags ?? 0}</span>
                        <span><strong>Currencies:</strong> {cacheCounts.currencies ?? 0}</span>
                      </div>
                      {!hasCache && (
                        <p className="text-muted mb-0 mt-2">
                          No cache yet. Click &quot;Refresh cache&quot; after connecting or when you
                          change organization, credentials, or data center.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <hr className="my-3" />

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 text-muted">Account Mapping</h6>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => setShowAccountEditModal(true)}
                disabled={isSubmitting || sortedAccountFields.length === 0}
              >
                <i className="fa fa-pencil me-1" />
                Edit mappings
              </button>
            </div>

            {sortedAccountFields.length === 0 ? (
              <p className="text-muted small mb-3">No account mapping definitions loaded.</p>
            ) : (
              <div className="border rounded mb-2" style={{ padding: '8px 12px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: `${COLUMN_GAP_PX}px`,
                  }}
                >
                  {renderMappingColumn(leftFields)}
                  {renderMappingColumn(rightFields)}
                </div>
                <hr className="my-3" />
                <AutoMappedFooterStrip />
              </div>
            )}

            <ZohoAccountMappingEditModal
              show={showAccountEditModal}
              onHide={() => setShowAccountEditModal(false)}
              fields={allAccountFields}
              accountMappings={accountMappings}
              onSaved={handleAccountMappingsSaved}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ZohoMappingsCard;
